import React, { useState, useEffect } from "react";
import { 
  Zap, 
  Play, 
  ShieldAlert, 
  BarChart3, 
  CheckSquare, 
  Check, 
  Pause,
  VolumeX, 
  TrendingUp, 
  Menu,
  X,
  Sparkles
} from "lucide-react";

export default function FocusFlow() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  
  // Interactive Mockup Dashboard States
  const [timerActive, setTimerActive] = useState(true);
  const [timeLeft, setTimeLeft] = useState(1499); // 24:59
  const [tasks, setTasks] = useState([
    { id: 1, text: "Refactor API endpoints", completed: true },
    { id: 2, text: "Design system styling tokens", completed: false },
    { id: 3, text: "Draft conversion copy", completed: false }
  ]);
  const [slackMuted, setSlackMuted] = useState(true);

  // Countdown timer simulation
  useEffect(() => {
    let interval = null;
    if (timerActive && timeLeft > 0) {
      interval = setInterval(() => {
        setTimeLeft((prevTime) => prevTime - 1);
      }, 1000);
    } else if (!timerActive) {
      clearInterval(interval);
    }
    return () => clearInterval(interval);
  }, [timerActive, timeLeft]);

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
  };

  const toggleTask = (id) => {
    setTasks(tasks.map(t => t.id === id ? { ...t, completed: !t.completed } : t));
  };

  return (
    <div className="min-h-screen bg-slate-950 text-zinc-100 selection:bg-indigo-500/30 selection:text-indigo-200 antialiased overflow-x-hidden font-sans">
      
      {/* Background Glow Orbs */}
      <div className="absolute top-0 left-1/4 w-[500px] h-[500px] bg-indigo-500/10 rounded-full blur-[120px] pointer-events-none -z-10" />
      <div className="absolute top-[800px] right-1/4 w-[600px] h-[600px] bg-purple-500/5 rounded-full blur-[150px] pointer-events-none -z-10" />
      <div className="absolute bottom-[500px] left-1/3 w-[500px] h-[500px] bg-indigo-500/5 rounded-full blur-[120px] pointer-events-none -z-10" />

      {/* Sticky Navigation Bar */}
      <nav className="sticky top-0 z-50 backdrop-blur-md bg-slate-950/80 border-b border-slate-900/60 transition-all duration-300">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          
          {/* Logo */}
          <a href="#" className="flex items-center gap-2 group">
            <div className="w-8 h-8 rounded-lg bg-indigo-600 flex items-center justify-center text-white shadow-md shadow-indigo-500/20 group-hover:bg-indigo-500 transition-colors duration-300">
              <Zap className="w-4 h-4 fill-white" />
            </div>
            <span className="text-xl font-bold tracking-tight text-white group-hover:text-indigo-400 transition-colors duration-300">
              FocusFlow
            </span>
          </a>

          {/* Desktop Links */}
          <div className="hidden md:flex items-center gap-8">
            <a href="#features" className="text-sm text-zinc-400 hover:text-white transition-colors duration-300">Features</a>
            <a href="#testimonials" className="text-sm text-zinc-400 hover:text-white transition-colors duration-300">Testimonials</a>
            <a href="#pricing" className="text-sm text-zinc-400 hover:text-white transition-colors duration-300">Pricing</a>
          </div>

          {/* Desktop Right CTA */}
          <div className="hidden md:flex items-center gap-4">
            <a href="#" className="text-sm text-zinc-400 hover:text-white transition-colors duration-300 px-3 py-1">
              Sign In
            </a>
            <a 
              href="#pricing" 
              className="text-sm font-semibold bg-indigo-600 hover:bg-indigo-500 text-white px-5 py-2 rounded-lg transition-all duration-300 shadow-lg shadow-indigo-500/20 hover:shadow-indigo-500/35 hover:-translate-y-[1px] active:translate-y-0"
            >
              Get FocusFlow
            </a>
          </div>

          {/* Mobile Menu Button */}
          <button 
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="md:hidden p-2 text-zinc-400 hover:text-white transition-colors duration-300"
          >
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile Navigation Dropdown */}
        {mobileMenuOpen && (
          <div className="md:hidden absolute top-16 left-0 w-full bg-slate-950 border-b border-slate-900 px-6 py-6 flex flex-col gap-4 animate-fadeIn">
            <a 
              href="#features" 
              onClick={() => setMobileMenuOpen(false)}
              className="text-lg text-zinc-400 hover:text-white transition-colors duration-300 py-1"
            >
              Features
            </a>
            <a 
              href="#testimonials" 
              onClick={() => setMobileMenuOpen(false)}
              className="text-lg text-zinc-400 hover:text-white transition-colors duration-300 py-1"
            >
              Testimonials
            </a>
            <a 
              href="#pricing" 
              onClick={() => setMobileMenuOpen(false)}
              className="text-lg text-zinc-400 hover:text-white transition-colors duration-300 py-1"
            >
              Pricing
            </a>
            <div className="h-[1px] bg-slate-900 my-2" />
            <a 
              href="#" 
              onClick={() => setMobileMenuOpen(false)}
              className="text-lg text-zinc-400 hover:text-white transition-colors duration-300 py-1"
            >
              Sign In
            </a>
            <a 
              href="#pricing"
              onClick={() => setMobileMenuOpen(false)}
              className="text-center font-semibold bg-indigo-600 hover:bg-indigo-500 text-white py-3 rounded-lg transition-all duration-300"
            >
              Get FocusFlow
            </a>
          </div>
        )}
      </nav>

      {/* Hero Section */}
      <section className="relative pt-20 pb-24 md:pt-28 md:pb-32 px-6">
        <div className="max-w-4xl mx-auto text-center flex flex-col items-center">
          
          {/* Badge */}
          <div className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full bg-indigo-950/50 border border-indigo-500/30 text-indigo-300 text-xs font-semibold tracking-wide uppercase mb-6 shadow-sm shadow-indigo-500/5 animate-pulse">
            <Sparkles className="w-3.5 h-3.5" />
            <span>Revolutionize your workday</span>
          </div>

          {/* Main Headline */}
          <h1 className="text-4xl sm:text-5xl md:text-6xl font-extrabold tracking-tight text-white leading-tight mb-6">
            Achieve your deep work state,{" "}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-indigo-400 via-indigo-200 to-purple-400">
              effortlessly.
            </span>
          </h1>

          {/* Sub-headline */}
          <p className="text-base sm:text-lg md:text-xl text-zinc-400 max-w-2xl leading-relaxed mb-10">
            FocusFlow blocks digital noise, automates your focus blocks, and tracks your daily productivity peaks so you can reclaim 2 hours every single day.
          </p>

          {/* Dual CTAs */}
          <div className="flex flex-col sm:flex-row gap-4 mb-20 w-full sm:w-auto">
            <a 
              href="#pricing"
              className="px-8 py-4 rounded-xl bg-indigo-600 hover:bg-indigo-500 text-white font-semibold transition-all duration-300 shadow-lg shadow-indigo-500/25 hover:shadow-indigo-500/40 hover:-translate-y-0.5 active:translate-y-0 text-center"
            >
              Start Free Trial
            </a>
            <a 
              href="#demo"
              className="px-8 py-4 rounded-xl bg-slate-900 hover:bg-slate-800/80 text-zinc-300 hover:text-white font-semibold border border-slate-800 hover:border-slate-700 transition-all duration-300 flex items-center justify-center gap-2 group text-center"
            >
              <Play className="w-4 h-4 fill-zinc-400 group-hover:fill-white text-zinc-400 group-hover:text-white transition-colors" />
              Watch 1-Min Demo
            </a>
          </div>

          {/* Interactive Mockup Container */}
          <div 
            id="demo"
            className="w-full max-w-5xl bg-slate-950 border border-slate-800/80 rounded-2xl p-3 sm:p-4 shadow-2xl shadow-indigo-500/5 relative group/mockup"
          >
            {/* Glossy sheen border */}
            <div className="absolute inset-0 bg-gradient-to-tr from-indigo-500/10 via-transparent to-purple-500/10 opacity-30 rounded-2xl pointer-events-none group-hover/mockup:opacity-40 transition-opacity duration-300" />
            
            {/* Dashboard Wrapper */}
            <div className="bg-slate-950 border border-slate-900 rounded-xl overflow-hidden shadow-inner text-left flex flex-col md:flex-row min-h-[350px]">
              
              {/* Sidebar Mockup */}
              <div className="w-full md:w-56 bg-slate-950 p-4 border-b md:border-b-0 md:border-r border-slate-900/80 flex flex-col justify-between">
                <div>
                  <div className="flex items-center gap-2 mb-6">
                    <div className="w-6 h-6 rounded bg-indigo-600 flex items-center justify-center text-white">
                      <Zap className="w-3.5 h-3.5 fill-white" />
                    </div>
                    <span className="font-semibold text-xs text-white">FocusFlow Console</span>
                  </div>
                  <div className="space-y-2">
                    <div className="px-3 py-2 rounded bg-slate-900 text-indigo-400 text-xs font-medium flex items-center justify-between">
                      <span>⚡ Dashboard</span>
                      <span className="w-1.5 h-1.5 rounded-full bg-indigo-400" />
                    </div>
                    <div className="px-3 py-2 rounded hover:bg-slate-900/40 text-zinc-500 hover:text-zinc-300 text-xs transition-colors duration-200">
                      📊 Session Stats
                    </div>
                    <div className="px-3 py-2 rounded hover:bg-slate-900/40 text-zinc-500 hover:text-zinc-300 text-xs transition-colors duration-200">
                      ⚙ Blocklist Settings
                    </div>
                  </div>
                </div>

                <div className="pt-4 mt-4 border-t border-slate-900/50">
                  <div className="flex items-center justify-between text-[11px] text-zinc-500">
                    <span>Slack Sync Status</span>
                    <span className="flex items-center gap-1.5 font-semibold text-indigo-400">
                      <span className="w-1.5 h-1.5 rounded-full bg-indigo-500 animate-ping" />
                      AUTOMATED
                    </span>
                  </div>
                </div>
              </div>

              {/* Main Dashboard Panel */}
              <div className="flex-1 p-6 flex flex-col md:flex-row gap-6">
                
                {/* Left Area: Timer & Slack Integration */}
                <div className="flex-1 flex flex-col justify-between">
                  <div>
                    <span className="text-[10px] text-indigo-400 uppercase tracking-widest font-semibold block mb-1">Active Flow block</span>
                    <h3 className="text-xl font-bold text-white mb-4">Deep Focus Blocks</h3>
                    
                    {/* Interactive Timer Box */}
                    <div className="bg-slate-900/40 border border-slate-900 rounded-xl p-6 text-center relative overflow-hidden">
                      <div className="absolute top-2 right-2">
                        <button 
                          onClick={() => setSlackMuted(!slackMuted)}
                          className={`p-1.5 rounded-lg border transition-all duration-300 ${slackMuted ? "bg-red-950/20 border-red-500/30 text-red-400" : "bg-zinc-900 border-zinc-800 text-zinc-400"}`}
                          title={slackMuted ? "Slack Notifications Blocked" : "Slack Notifications Muted"}
                        >
                          <VolumeX className="w-4 h-4" />
                        </button>
                      </div>
                      <div className="text-4xl font-extrabold text-white tracking-widest font-mono mb-3">
                        {formatTime(timeLeft)}
                      </div>
                      <div className="flex items-center justify-center gap-3">
                        <button 
                          onClick={() => setTimerActive(!timerActive)}
                          className="px-4 py-1.5 rounded-lg bg-indigo-600 hover:bg-indigo-500 text-white text-xs font-semibold flex items-center gap-1.5 transition-colors"
                        >
                          {timerActive ? (
                            <>
                              <Pause className="w-3.5 h-3.5 fill-white" /> Pause Block
                            </>
                          ) : (
                            <>
                              <Play className="w-3.5 h-3.5 fill-white" /> Resume Block
                            </>
                          )}
                        </button>
                        <button 
                          onClick={() => setTimeLeft(1500)}
                          className="px-3 py-1.5 rounded-lg bg-slate-800 hover:bg-slate-700 text-zinc-300 text-xs font-medium transition-colors"
                        >
                          Reset
                        </button>
                      </div>
                    </div>
                  </div>

                  <div className="mt-4 p-3.5 bg-slate-900/20 border border-slate-900/60 rounded-xl flex items-center gap-3">
                    <div className={`p-2 rounded-lg ${slackMuted ? "bg-red-500/10 text-red-400" : "bg-indigo-500/10 text-indigo-400"}`}>
                      <ShieldAlert className="w-5 h-5" />
                    </div>
                    <div>
                      <p className="text-xs text-white font-medium">Distraction Shield Active</p>
                      <p className="text-[11px] text-zinc-500">Muted Slack notifications & blocked 12 distracting sites.</p>
                    </div>
                  </div>
                </div>

                {/* Right Area: Task List & Peak Analytics */}
                <div className="w-full md:w-64 flex flex-col justify-between gap-4">
                  
                  {/* Task list integration */}
                  <div className="bg-slate-900/30 border border-slate-900/80 rounded-xl p-4">
                    <span className="text-[10px] text-zinc-500 uppercase tracking-widest font-semibold block mb-2.5">Bind Tasks to Session</span>
                    <div className="space-y-2">
                      {tasks.map(task => (
                        <div 
                          key={task.id} 
                          onClick={() => toggleTask(task.id)}
                          className="flex items-center gap-2 px-2.5 py-2 rounded bg-slate-900/60 hover:bg-slate-900 border border-slate-900 hover:border-slate-800/80 cursor-pointer transition-all duration-150"
                        >
                          <div className={`w-4 h-4 rounded border flex items-center justify-center transition-all ${task.completed ? "bg-indigo-600 border-indigo-500 text-white" : "border-slate-700 text-transparent"}`}>
                            <Check className="w-3 h-3 stroke-[3px]" />
                          </div>
                          <span className={`text-xs select-none transition-all ${task.completed ? "line-through text-zinc-600" : "text-zinc-300"}`}>
                            {task.text}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Flow Analytics widget preview */}
                  <div className="bg-slate-900/30 border border-slate-900/80 rounded-xl p-4">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-[10px] text-zinc-500 uppercase tracking-widest font-semibold">Today's Focus Trend</span>
                      <TrendingUp className="w-3.5 h-3.5 text-indigo-400" />
                    </div>
                    <div className="flex items-end gap-1.5 h-16 pt-2">
                      <div className="flex-1 bg-slate-800 rounded-t h-[30%] hover:bg-indigo-600 transition-all duration-300" title="9:00 AM - 30% Focus" />
                      <div className="flex-1 bg-slate-800 rounded-t h-[50%] hover:bg-indigo-600 transition-all duration-300" title="11:00 AM - 50% Focus" />
                      <div className="flex-1 bg-indigo-500 rounded-t h-[95%] relative hover:bg-indigo-400 transition-all duration-300" title="2:00 PM - Peak Flow state (95%)">
                        <span className="absolute -top-4 left-1/2 -translate-x-1/2 text-[8px] bg-indigo-500 text-white px-1 rounded font-bold">PEAK</span>
                      </div>
                      <div className="flex-1 bg-slate-800 rounded-t h-[65%] hover:bg-indigo-600 transition-all duration-300" title="4:00 PM - 65% Focus" />
                      <div className="flex-1 bg-slate-800 rounded-t h-[40%] hover:bg-indigo-600 transition-all duration-300" title="6:00 PM - 40% Focus" />
                    </div>
                  </div>

                </div>

              </div>

            </div>
          </div>

        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-24 border-t border-slate-900 bg-slate-950 px-6 relative">
        <div className="max-w-7xl mx-auto">
          
          <div className="text-center max-w-3xl mx-auto mb-16">
            <span className="text-indigo-400 font-bold text-xs uppercase tracking-widest">Built for high performance</span>
            <h2 className="text-3xl sm:text-4xl font-extrabold text-white mt-2 mb-4">
              Block digital clutter. Reclaim your focus.
            </h2>
            <p className="text-zinc-400 text-base sm:text-lg">
              FocusFlow connects smoothly to your existing tools and manages notifications in the background so you can concentrate.
            </p>
          </div>

          {/* 3-Column Features Grid */}
          <div className="grid md:grid-cols-3 gap-8">
            
            {/* Feature 1 */}
            <div className="group bg-slate-900/30 hover:bg-slate-900/50 border border-slate-900 hover:border-slate-800/80 rounded-2xl p-8 transition-all duration-300 hover:-translate-y-1">
              <div className="w-12 h-12 rounded-xl bg-indigo-500/10 text-indigo-400 flex items-center justify-center mb-6 group-hover:bg-indigo-600 group-hover:text-white transition-all duration-300">
                <ShieldAlert className="w-6 h-6" />
              </div>
              <h3 className="text-xl font-bold text-white mb-3">Intelligent Distraction Shield</h3>
              <p className="text-zinc-400 text-sm leading-relaxed">
                Automatically mutes Slack, social media notifications, and customized blacklists the moment you start a flow block. Zero configuration required.
              </p>
            </div>

            {/* Feature 2 */}
            <div className="group bg-slate-900/30 hover:bg-slate-900/50 border border-slate-900 hover:border-slate-800/80 rounded-2xl p-8 transition-all duration-300 hover:-translate-y-1">
              <div className="w-12 h-12 rounded-xl bg-indigo-500/10 text-indigo-400 flex items-center justify-center mb-6 group-hover:bg-indigo-600 group-hover:text-white transition-all duration-300">
                <BarChart3 className="w-6 h-6" />
              </div>
              <h3 className="text-xl font-bold text-white mb-3">Flow Analytics & Insights</h3>
              <p className="text-zinc-400 text-sm leading-relaxed">
                Deep dive into your peak productivity hours, daily focus streaks, and focus history charts to identify exactly when you work best.
              </p>
            </div>

            {/* Feature 3 */}
            <div className="group bg-slate-900/30 hover:bg-slate-900/50 border border-slate-900 hover:border-slate-800/80 rounded-2xl p-8 transition-all duration-300 hover:-translate-y-1">
              <div className="w-12 h-12 rounded-xl bg-indigo-500/10 text-indigo-400 flex items-center justify-center mb-6 group-hover:bg-indigo-600 group-hover:text-white transition-all duration-300">
                <CheckSquare className="w-6 h-6" />
              </div>
              <h3 className="text-xl font-bold text-white mb-3">Task-to-Timer Integration</h3>
              <p className="text-zinc-400 text-sm leading-relaxed">
                Map out your daily to-do list and bind tasks directly to custom Pomodoro or time-boxed sessions. Track completion logs automatically.
              </p>
            </div>

          </div>

        </div>
      </section>

      {/* Social Proof / Testimonials Section */}
      <section id="testimonials" className="py-24 border-t border-slate-900 bg-slate-950/60 px-6">
        <div className="max-w-7xl mx-auto">
          
          <div className="text-center max-w-2xl mx-auto mb-16">
            <span className="text-indigo-400 font-bold text-xs uppercase tracking-widest">Loved by builders & creators</span>
            <h2 className="text-3xl sm:text-4xl font-extrabold text-white mt-2">
              Proven productivity increases.
            </h2>
          </div>

          {/* Testimonial Cards */}
          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            
            {/* Card 1 */}
            <div className="bg-slate-900/40 border border-slate-900 rounded-2xl p-8 hover:border-slate-800 transition-colors duration-300 flex flex-col justify-between">
              <p className="text-zinc-300 italic text-base leading-relaxed mb-6">
                "FocusFlow saved my semester. I went from pulling stressful all-nighters to finishing my coding projects days before the deadline."
              </p>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-slate-800 border border-indigo-500/20 overflow-hidden flex items-center justify-center text-xs font-semibold text-indigo-300">
                  SK
                </div>
                <div>
                  <h4 className="text-white font-bold text-sm">Sarah K.</h4>
                  <p className="text-zinc-500 text-xs">Computer Science Student</p>
                </div>
              </div>
            </div>

            {/* Card 2 */}
            <div className="bg-slate-900/40 border border-slate-900 rounded-2xl p-8 hover:border-slate-800 transition-colors duration-300 flex flex-col justify-between">
              <p className="text-zinc-300 italic text-base leading-relaxed mb-6">
                "The automated Slack sync is a game-changer. It silences my notifications instantly so I can write code without getting yanked out of the zone."
              </p>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-slate-800 border border-indigo-500/20 overflow-hidden flex items-center justify-center text-xs font-semibold text-indigo-300">
                  DM
                </div>
                <div>
                  <h4 className="text-white font-bold text-sm">David M.</h4>
                  <p className="text-zinc-500 text-xs">Software Engineer</p>
                </div>
              </div>
            </div>

          </div>

        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="py-24 border-t border-slate-900 bg-slate-950 px-6">
        <div className="max-w-7xl mx-auto">
          
          <div className="text-center max-w-2xl mx-auto mb-16">
            <span className="text-indigo-400 font-bold text-xs uppercase tracking-widest">Simple transparent pricing</span>
            <h2 className="text-3xl sm:text-4xl font-extrabold text-white mt-2 mb-4">
              Unlock your focus potential.
            </h2>
            <p className="text-zinc-400 text-sm sm:text-base">
              Start completely free, then upgrade to Pro when you need advanced features and native workspace extensions.
            </p>
          </div>

          {/* Pricing Grid */}
          <div className="grid md:grid-cols-2 gap-8 max-w-3xl mx-auto">
            
            {/* Starter Tier */}
            <div className="bg-slate-900/20 border border-slate-900 rounded-2xl p-8 hover:border-slate-800/80 transition-all duration-300 flex flex-col justify-between">
              <div>
                <div className="mb-6">
                  <h4 className="text-zinc-400 font-semibold text-sm uppercase tracking-wide">Starter</h4>
                  <div className="flex items-baseline gap-1 mt-2 text-white">
                    <span className="text-4xl font-extrabold">$0</span>
                    <span className="text-zinc-500 text-sm">/ forever</span>
                  </div>
                </div>
                <ul className="space-y-4 mb-8">
                  <li className="flex items-center gap-2.5 text-sm text-zinc-300">
                    <Check className="w-4 h-4 text-indigo-500 stroke-[3px]" />
                    <span>2 daily focus sessions</span>
                  </li>
                  <li className="flex items-center gap-2.5 text-sm text-zinc-300">
                    <Check className="w-4 h-4 text-indigo-500 stroke-[3px]" />
                    <span>Basic website blocking (max 5 apps)</span>
                  </li>
                  <li className="flex items-center gap-2.5 text-sm text-zinc-300">
                    <Check className="w-4 h-4 text-indigo-500 stroke-[3px]" />
                    <span>7-day metrics & history logs</span>
                  </li>
                </ul>
              </div>
              <a 
                href="#download"
                className="w-full text-center py-3 px-4 bg-slate-900 hover:bg-slate-800 text-white font-medium rounded-xl border border-slate-800 hover:border-slate-700 transition-all duration-300"
              >
                Get Started
              </a>
            </div>

            {/* Pro Tier (Highlighted Card) */}
            <div className="bg-slate-900/40 border-2 border-indigo-500/80 rounded-2xl p-8 relative shadow-2xl shadow-indigo-500/5 transition-all duration-300 hover:border-indigo-400/90 flex flex-col justify-between">
              {/* Most Popular Badge */}
              <span className="absolute -top-3.5 left-1/2 -translate-x-1/2 bg-indigo-600 text-white text-xs font-bold px-3 py-1 rounded-full uppercase tracking-wider shadow-md shadow-indigo-600/20">
                Most Popular
              </span>
              
              <div>
                <div className="mb-6">
                  <h4 className="text-indigo-400 font-semibold text-sm uppercase tracking-wide">Pro</h4>
                  <div className="flex items-baseline gap-1 mt-2 text-white">
                    <span className="text-4xl font-extrabold">$8</span>
                    <span className="text-zinc-500 text-sm">/ month</span>
                  </div>
                </div>
                <ul className="space-y-4 mb-8">
                  <li className="flex items-center gap-2.5 text-sm text-zinc-100">
                    <Check className="w-4 h-4 text-indigo-400 stroke-[3px]" />
                    <span>Unlimited focus sessions</span>
                  </li>
                  <li className="flex items-center gap-2.5 text-sm text-zinc-100">
                    <Check className="w-4 h-4 text-indigo-400 stroke-[3px]" />
                    <span>Smart adaptive block scheduling</span>
                  </li>
                  <li className="flex items-center gap-2.5 text-sm text-zinc-100">
                    <Check className="w-4 h-4 text-indigo-400 stroke-[3px]" />
                    <span>Lifetime metrics with CSV exports</span>
                  </li>
                  <li className="flex items-center gap-2.5 text-sm text-zinc-100">
                    <Check className="w-4 h-4 text-indigo-400 stroke-[3px]" />
                    <span>Cross-device real-time sync</span>
                  </li>
                  <li className="flex items-center gap-2.5 text-sm text-zinc-100">
                    <Check className="w-4 h-4 text-indigo-400 stroke-[3px]" />
                    <span>Native Slack & Notion Integrations</span>
                  </li>
                </ul>
              </div>
              <a 
                href="#download"
                className="w-full text-center py-3 px-4 bg-indigo-600 hover:bg-indigo-500 hover:shadow-lg hover:shadow-indigo-500/20 text-white font-semibold rounded-xl transition-all duration-300"
              >
                Upgrade to Pro
              </a>
            </div>

          </div>

        </div>
      </section>

      {/* Final Download CTA Footer Section */}
      <section id="download" className="py-20 bg-slate-950 px-6">
        <div className="max-w-4xl mx-auto">
          
          <div className="bg-gradient-to-r from-indigo-900 via-indigo-950 to-purple-900 rounded-3xl p-8 sm:p-12 text-center border border-indigo-500/20 shadow-2xl shadow-indigo-500/10 relative overflow-hidden group">
            
            {/* Animated decoration inside CTA banner */}
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_bottom_left,rgba(99,102,241,0.15),transparent)] pointer-events-none" />
            
            <h2 className="text-2xl sm:text-3xl font-extrabold text-white mb-3">
              Ready to eliminate distractions?
            </h2>
            <p className="text-indigo-200 text-sm sm:text-base max-w-xl mx-auto mb-8 leading-relaxed">
              Join thousands of software engineers, researchers, and writers who use FocusFlow to achieve high-output flow states every day.
            </p>

            <button className="inline-flex items-center gap-2 bg-white hover:bg-zinc-100 text-slate-950 font-bold px-8 py-4 rounded-xl shadow-lg transition-all duration-300 hover:shadow-xl hover:shadow-white/5 hover:-translate-y-0.5 active:translate-y-0 text-sm sm:text-base">
              
              {/* Apple Custom SVG */}
              <svg className="w-4 h-4 fill-slate-950" viewBox="0 0 24 24">
                <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M15.97 4.17c.66-.81 1.11-1.93.99-3.06-1 .04-2.22.67-2.94 1.5-.63.73-1.18 1.87-1.03 2.98.11 0 2.32-.61 2.98-1.42z" />
              </svg>

              {/* Windows Custom SVG */}
              <svg className="w-4 h-4 fill-slate-950" viewBox="0 0 24 24">
                <path d="M0 3.449L9.75 2.1v9.45H0V3.449zM0 12.45h9.75v9.45L0 20.551v-8.1zM11.25 1.9L24 0v11.55H11.25V1.9zM11.25 12.45H24v11.55l-12.75-1.9v-9.65z" />
              </svg>

              Download for Desktop & Mobile
            </button>
            
          </div>

          {/* Real footer text */}
          <div className="mt-16 text-center border-t border-slate-900/60 pt-8 flex flex-col sm:flex-row justify-between items-center gap-4 text-xs text-zinc-500">
            <p>© 2026 FocusFlow Inc. Built for deep thinkers.</p>
            <div className="flex gap-6">
              <a href="#" className="hover:text-zinc-300 transition-colors">Privacy Policy</a>
              <a href="#" className="hover:text-zinc-300 transition-colors">Terms of Service</a>
              <a href="#" className="hover:text-zinc-300 transition-colors">Contact Support</a>
            </div>
          </div>

        </div>
      </section>

    </div>
  );
}
